import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { useWatch } from "react-hook-form";
import { CheckCircleIcon } from "@heroicons/react/24/outline";

import FormTitle from "components/Form/Title";
import FormErrors from "pages/ApplicationForm/components/FormErrors";
import { APPLICATION_ERROR_CODES } from "utils/consts";
import { StepProps } from "../../types";
import { LABELS } from "./labels";
import { QUERY_ELIGIBLE_PRODUCTS } from "api/queries/application";
import useApplicationStore from "store/Application";
import Loading from "components/Loading";
import { MoneyFormatCurrencyStyle } from "utils/calcs";
import { WEB_URLS } from "settings";

const STEP_KEY = "product";

const PRODUCT_STATS: {
  label: string;
  key: string;
  currency?: boolean;
  fallback?: string;
  suffix?: string;
}[] = [
  { label: LABELS.fixedTermCopy, key: "fixedTermCopy" },
  { label: LABELS.initialPeriodIO, key: "initialPeriodInterestOnly" },
  { label: LABELS.initialRate, key: "initialRate", suffix: "%" },
  { label: LABELS.monthlyPayment, key: "monthlyPayment", currency: true },
  { label: LABELS.aprc, key: "aprc", suffix: "%" },
  {
    label: LABELS.reversionRate,
    key: "reversionRate",
    fallback: "-",
    suffix: "%",
  },
  {
    label: LABELS.reversionPayment,
    key: "reversionPayment",
    currency: true,
    fallback: "-",
  },
  { label: LABELS.maxLTV, key: "maxLtv", suffix: "%" },
  { label: LABELS.valuationType, key: "valuationType" },
  { label: LABELS.lenderFee, key: "lenderFee", currency: true },
];

const StatItem = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="bg-white px-4 py-3">
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-lg font-semibold text-gray-900">{value}</dd>
  </div>
);

const ProductForm: React.FC<StepProps> = ({ control, errors, setValue }) => {
  const [selectedProductName, setSelectedProductName] = useState<string | null>(
    null,
  );

  const {
    applicationId: applicationUuid,
    applicationErrors,
    setPricingExpiryDate,
  } = useApplicationStore();

  const pricingExpiredError = applicationErrors?.find(
    (e) => e.code === APPLICATION_ERROR_CODES.PRODUCT_PRICING_EXPIRED,
  );

  const { data: eligibleProducts, loading } = useQuery(
    QUERY_ELIGIBLE_PRODUCTS,
    {
      variables: {
        applicationUuid,
      },
      fetchPolicy: "no-cache",
      onCompleted: (data) => {
        if (!applicationUuid) {
          return;
        }

        setPricingExpiryDate(
          data?.data?.eligibleProducts?.pricingExpiryDate ?? null,
        );
      },
    },
  );

  const currentProduct = useWatch({
    name: `${STEP_KEY}.selectedProductUuid`,
    control: control,
  });

  const eligibleProductList =
    eligibleProducts?.data?.eligibleProducts?.products;

  const handleSelectProduct = (
    selectedProductUuid: string,
    selectedProductName: string,
  ) => {
    setSelectedProductName(selectedProductName);
    setValue(STEP_KEY, {
      selectedProductUuid,
    });
  };

  const selectedProductDisplay =
    selectedProductName ||
    eligibleProductList?.find((product: any) => product.uuid === currentProduct)
      ?.code;

  const renderProductStats = (product: any) => {
    return PRODUCT_STATS.map(({ label, key, currency, fallback, suffix }) => {
      const rawValue = product[key];
      const value = rawValue
        ? currency
          ? MoneyFormatCurrencyStyle.format(rawValue)
          : `${rawValue}${suffix || ""}`
        : fallback || rawValue;
      return <StatItem key={key} label={label} value={value} />;
    });
  };

  const generateProductIllustration = (productUuid: string) => {
    window.open(
      `${WEB_URLS.DOCUMENT_SERVER}download/application/${applicationUuid}/illustration/${productUuid}`,
      "_blank",
      "noopener",
    );
  };

  return (
    <div className="flex flex-col space-y-4">
      <FormTitle left border title="Eligible Products" mb={false} />

      <FormErrors
        formErrors={
          pricingExpiredError
            ? { "": { message: pricingExpiredError.message, type: "root" } }
            : errors?.[STEP_KEY]?.selectedProductUuid
            ? [{ selectedProduct: errors?.[STEP_KEY]?.selectedProductUuid }]
            : null
        }
      />

      {loading ? (
        <div className="flex flex-col items-center justify-center pt-4">
          <Loading />
          <p className="mt-4">Loading eligible products...</p>
        </div>
      ) : (
        <>
          {eligibleProductList?.length === 0 ? (
            <div className="pt-4 text-center">
              <p>No eligible products found</p>
            </div>
          ) : (
            <>
              <FormTitle
                left
                title={`Selected Product: ${selectedProductDisplay || "-"}`}
                mb
              />

              <div className="flex flex-col space-y-6 pt-4">
                {eligibleProductList?.map((product: any) => (
                  <div
                    key={product.uuid}
                    className="rounded-md border border-gray-300"
                  >
                    <div className="border-b border-gray-300 bg-gray-100 px-4 py-3">
                      <h3 className="text-lg font-bold">{product.code}</h3>
                    </div>
                    <dl className="grid grid-cols-2 gap-px bg-gray-200 lg:grid-cols-5">
                      {renderProductStats(product)}
                    </dl>
                    <div className="border-b border-t border-gray-300 px-4 py-3">
                      <dt className="text-sm font-medium text-gray-500">
                        {LABELS.ercsCopy}
                      </dt>
                      <dd className="mt-1 text-lg font-semibold text-gray-900">
                        {product.ercsCopy || "N/A"}
                      </dd>
                    </div>
                    <div className="px-4 py-3">
                      <div className="flex justify-end">
                        <>
                          <button
                            type="button"
                            onClick={() =>
                              generateProductIllustration(product.uuid)
                            }
                            className="link font-medium underline"
                          >
                            Generate ESIS
                          </button>

                          <span className="mx-2 text-gray-900"> | </span>

                          {currentProduct === product.uuid ? (
                            <div className="flex items-center gap-1 text-green-800">
                              <CheckCircleIcon className="inline h-6 w-6 self-center" />
                              <span>Selected</span>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() =>
                                handleSelectProduct(product.uuid, product.code)
                              }
                              className="link font-medium underline"
                            >
                              Select Product
                            </button>
                          )}
                        </>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ProductForm;
